import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, HelpCircle, Send, Loader2, User, Bot, Settings, Wand2, Bug, Headphones, Inbox, ChevronDown, ChevronRight, Globe, Mic } from 'lucide-react';
import { OPEN_DOMAIN_HELPER_EVENT } from './DomainSetupHelperModal';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import { useAppSystem } from '../hooks/useAppSystem';
import ReportBugModal from './ReportBugModal';
import SupportRequestModal from './SupportRequestModal';
import TokenPurchaseModal from './TokenPurchaseModal';
import { useVoiceHelp } from '../contexts/VoiceHelpContext';
import { SETUP_CATEGORIES, servicesBookingItems } from './AISetupAssistantModal';
import {
  describeAssistantAction,
  executeAssistantAction,
  getAssistantActionCatalog,
} from '../lib/assistant/appActions';
import { captureScreenshot } from '../lib/assistant/screenCapture';
import { getOpenAssistantSurfaces } from '../lib/assistant/surface';
import { GHOST_CHOICE_EVENT, type GhostChoice } from '../lib/assistant/customerLookup';

/** A user's own support request or bug report, unified for the Recent requests list. */
interface MyTicket {
  id: string;
  /** Raw DB row id (no kind prefix). */
  rawId: string;
  kind: 'support' | 'bug';
  /** Human-referenceable ticket number (support requests only), e.g. NX-7KQ2MW. */
  ticketNumber: string | null;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  adminReply: string | null;
  repliedAt: string | null;
}

interface TicketMessage {
  id: string;
  sender: 'user' | 'admin';
  body: string;
  created_at: string;
}

const TICKET_STATUS_META: Record<string, { label: string; cls: string }> = {
  // support_requests
  open: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
  replied: { label: 'Replied', cls: 'bg-blue-100 text-blue-700' },
  closed: { label: 'Closed', cls: 'bg-gray-100 text-gray-600' },
  // user_bugs
  new: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
  investigating: { label: 'In progress', cls: 'bg-blue-100 text-blue-700' },
  fixed: { label: 'Fixed', cls: 'bg-green-100 text-green-700' },
};

interface HelpMessage {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: string[];
  /** App-control actions the assistant performed with this answer (confirmation chips). */
  actionsTaken?: Array<{ label: string; ok: boolean }>;
}

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSetupWizard?: () => void;
}

export default function HelpModal({ isOpen, onClose, onOpenSetupWizard }: HelpModalProps) {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const { coreApps, installedAddons } = useAppSystem();
  const installedAppNames = useMemo(
    () =>
      [...(coreApps || []), ...(installedAddons || [])]
        .map((a) => a.app?.name)
        .filter((name): name is string => Boolean(name)),
    [coreApps, installedAddons]
  );
  const hasCalendarApp = useMemo(
    () =>
      (coreApps || []).some((a) => a.app?.slug === 'calendar') ||
      (installedAddons || []).some((a) => a.app?.slug === 'calendar'),
    [coreApps, installedAddons]
  );
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [isReportBugOpen, setIsReportBugOpen] = useState(false);
  const [isSupportRequestOpen, setIsSupportRequestOpen] = useState(false);

  // Setup Wizard progress badge (e.g. "3/7"): a category counts as done when
  // every step in it is completed or skipped — same rule the wizard itself uses.
  const [wizardProgress, setWizardProgress] = useState<{ done: number; total: number } | null>(null);
  const loadWizardProgress = React.useCallback(async () => {
    if (!supabase || !selectedBusiness?.id) return;
    const total = SETUP_CATEGORIES.length;
    try {
      const { data } = await supabase
        .from('ai_setup_wizard_progress')
        .select('completed, responses')
        .eq('business_id', selectedBusiness.id)
        .maybeSingle();

      if (!data) {
        setWizardProgress({ done: 0, total });
        return;
      }
      if (data.completed) {
        setWizardProgress({ done: total, total });
        return;
      }

      const responses = (data.responses || {}) as {
        completed_items?: Record<string, string[]>;
        skipped_items?: Record<string, string[]>;
        online_booking_answer?: 'yes' | 'no' | null;
      };
      const completedMap = responses.completed_items || {};
      const skippedMap = responses.skipped_items || {};
      const answer =
        responses.online_booking_answer === 'yes' || responses.online_booking_answer === 'no'
          ? responses.online_booking_answer
          : null;

      let done = 0;
      for (const cat of SETUP_CATEGORIES) {
        const items = cat.id === 'services-booking' ? servicesBookingItems(answer) : cat.items;
        if (items.length === 0) continue;
        const handledIds = new Set([...(completedMap[cat.id] || []), ...(skippedMap[cat.id] || [])]);
        if (items.every((item) => handledIds.has(item.id))) done += 1;
      }
      setWizardProgress({ done, total });
    } catch (e) {
      console.warn('Failed to load setup wizard progress:', e);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (isOpen) void loadWizardProgress();
  }, [isOpen, loadWizardProgress]);

  // Live voice conversation: starts the session, then closes this panel so the
  // user can navigate the app while talking (Stop AI lives in the top nav).
  const { status: voiceStatus, error: voiceError, startVoice } = useVoiceHelp();
  const handleStartVoice = async () => {
    const ok = await startVoice(installedAppNames, selectedBusiness?.id);
    if (ok) onClose();
  };

  // Ghost-card taps during a TEXT chat: relay the pick/cancel into the
  // conversation as a user message. When a voice session is live it handles
  // the tap itself (VoiceHelpContext), so this stays out of the way. Taps
  // that land while the agent loop is still running are held and sent the
  // moment it finishes, so the flow continues without re-prompting.
  const ghostChoiceGate = useRef<{ decide: () => 'send' | 'queue' | 'ignore'; send: (q: string) => void }>({
    decide: () => 'ignore',
    send: () => {},
  });
  const pendingGhostChoice = useRef<string | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const choice = (e as CustomEvent).detail as GhostChoice | undefined;
      if (!choice) return;
      const verdict = ghostChoiceGate.current.decide();
      if (verdict === 'ignore') return;
      const text =
        choice.kind === 'selected'
          ? `I tapped the card for ${choice.customer.name}${choice.customer.email ? ` (${choice.customer.email})` : choice.customer.phone ? ` (${choice.customer.phone})` : ''} — customer_id ${choice.customer.id}. Use this customer and continue my original request, including every detail I already gave (service, price, notes...).`
          : 'I closed the customer match cards — none of them. Cancel that operation.';
      if (verdict === 'queue') {
        pendingGhostChoice.current = text;
        return;
      }
      ghostChoiceGate.current.send(text);
    };
    window.addEventListener(GHOST_CHOICE_EVENT, handler);
    return () => window.removeEventListener(GHOST_CHOICE_EVENT, handler);
  }, []);
  useEffect(() => {
    if (loading || !pendingGhostChoice.current) return;
    const text = pendingGhostChoice.current;
    pendingGhostChoice.current = null;
    ghostChoiceGate.current.send(text);
  }, [loading]);

  // "Configure Custom Domain" shows only while a connected website still runs
  // on the temporary URL (Vercel project linked, no real domain saved yet).
  const heldSiteUrl = ((selectedBusiness as any)?.nexus_website_url || '').toString().trim();
  const needsDomainSetup =
    Boolean(((selectedBusiness as any)?.vercel_project_id || '').toString().trim()) &&
    (!heldSiteUrl || heldSiteUrl.includes('.vercel.app'));

  // ── Recent requests: the user's own support requests + bug reports ──
  const [myTickets, setMyTickets] = useState<MyTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [ticketTab, setTicketTab] = useState<'open' | 'closed'>('open');

  // "Closed" = resolved tickets (closed support requests, fixed bugs); everything else is open
  const isClosedTicket = (t: MyTicket) => t.status === 'closed' || t.status === 'fixed';
  const visibleTickets = myTickets.filter((t) =>
    ticketTab === 'closed' ? isClosedTicket(t) : !isClosedTicket(t)
  );
  const closedCount = myTickets.filter(isClosedTicket).length;
  const openCount = myTickets.length - closedCount;

  const loadMyTickets = React.useCallback(async () => {
    if (!supabase || !user?.id) return;
    setTicketsLoading(true);
    try {
      const [supportRes, bugsRes] = await Promise.all([
        supabase
          .from('support_requests')
          .select('id, ticket_number, subject, description, status, created_at, admin_reply, replied_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(25),
        supabase
          .from('user_bugs')
          .select('id, description, status, created_at, admin_reply, replied_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(25),
      ]);

      const tickets: MyTicket[] = [
        ...((supportRes.data || []) as any[]).map((r) => ({
          id: `support_${r.id}`,
          rawId: r.id,
          kind: 'support' as const,
          ticketNumber: r.ticket_number ?? null,
          title: r.subject || 'Support request',
          description: r.description || '',
          status: r.status || 'open',
          createdAt: r.created_at,
          adminReply: r.admin_reply ?? null,
          repliedAt: r.replied_at ?? null,
        })),
        ...((bugsRes.data || []) as any[]).map((r) => ({
          id: `bug_${r.id}`,
          rawId: r.id,
          kind: 'bug' as const,
          ticketNumber: null,
          title: r.description || 'Bug report',
          description: r.description || '',
          status: r.status || 'new',
          createdAt: r.created_at,
          adminReply: r.admin_reply ?? null,
          repliedAt: r.replied_at ?? null,
        })),
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setMyTickets(tickets);
    } catch (e) {
      console.warn('Failed to load recent requests:', e);
    } finally {
      setTicketsLoading(false);
    }
  }, [user?.id]);

  // Load on open; reload when a submit modal closes (a new ticket may exist).
  useEffect(() => {
    if (isOpen) void loadMyTickets();
  }, [isOpen, loadMyTickets]);
  useEffect(() => {
    if (isOpen && !isReportBugOpen && !isSupportRequestOpen) void loadMyTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReportBugOpen, isSupportRequestOpen]);

  // ── Support ticket conversation thread (loaded on expand) ──
  const [threadByTicket, setThreadByTicket] = useState<Record<string, TicketMessage[]>>({});
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadDraft, setThreadDraft] = useState('');
  const [threadSending, setThreadSending] = useState(false);

  const loadThread = React.useCallback(async (requestId: string) => {
    if (!supabase) return;
    setThreadLoading(true);
    try {
      const { data, error } = await supabase
        .from('support_request_messages')
        .select('id, sender, body, created_at')
        .eq('request_id', requestId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setThreadByTicket((prev) => ({ ...prev, [requestId]: (data || []) as TicketMessage[] }));
    } catch (e) {
      console.warn('Failed to load ticket thread:', e);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  const sendThreadMessage = async (ticket: MyTicket) => {
    if (!supabase || !user?.id || !threadDraft.trim()) return;
    setThreadSending(true);
    try {
      const { error } = await supabase.from('support_request_messages').insert({
        request_id: ticket.rawId,
        sender: 'user',
        sender_user_id: user.id,
        body: threadDraft.trim(),
      });
      if (error) throw error;
      setThreadDraft('');
      await Promise.all([loadThread(ticket.rawId), loadMyTickets()]);
    } catch (e) {
      console.error('Failed to send message:', e);
    } finally {
      setThreadSending(false);
    }
  };

  const expandTicket = (t: MyTicket, expanded: boolean) => {
    setExpandedTicketId(expanded ? null : t.id);
    setThreadDraft('');
    if (!expanded && t.kind === 'support') void loadThread(t.rawId);
  };

  const [showLocationBookingInFAB, setShowLocationBookingInFAB] = useState(true);
  const [showMobileBookingInFAB, setShowMobileBookingInFAB] = useState(true);
  const [defaultCalendarView, setDefaultCalendarView] = useState<'month' | 'week' | 'day'>('month');
  const [bookingsByServiceAreaEnabled, setBookingsByServiceAreaEnabled] = useState(true);
  const [quickSettingsLoading, setQuickSettingsLoading] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const showCalendarFabToolInChat = messages.some(
    (m) => m.role === 'assistant' && m.toolCalls?.includes('show_calendar_fab_settings')
  );
  const showDefaultCalendarViewToolInChat = messages.some(
    (m) => m.role === 'assistant' && m.toolCalls?.includes('show_default_calendar_view_setting')
  );
  const showBookingsByServiceAreaToolInChat = messages.some(
    (m) => m.role === 'assistant' && m.toolCalls?.includes('show_bookings_by_service_area_setting')
  );

  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id || !hasCalendarApp || !supabase || !showCalendarFabToolInChat) return;
    const load = async () => {
      setQuickSettingsLoading(true);
      try {
        const [loc, mobile] = await Promise.all([
          supabase.rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_location_booking_in_fab',
            p_default_value: true,
          }),
          supabase.rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'calendar',
            p_setting_key: 'show_mobile_booking_in_fab',
            p_default_value: true,
          }),
        ]);
        if (loc.data !== null && loc.data !== undefined) setShowLocationBookingInFAB(Boolean(loc.data));
        if (mobile.data !== null && mobile.data !== undefined) setShowMobileBookingInFAB(Boolean(mobile.data));
      } catch {
        // keep defaults
      } finally {
        setQuickSettingsLoading(false);
      }
    };
    load();
  }, [isOpen, selectedBusiness?.id, hasCalendarApp, showCalendarFabToolInChat]);

  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id || !hasCalendarApp || !supabase || !showDefaultCalendarViewToolInChat) return;
    const load = async () => {
      setQuickSettingsLoading(true);
      try {
        const { data } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'default_calendar_view',
          p_default_value: 'month',
        });
        if (data === 'month' || data === 'week' || data === 'day') setDefaultCalendarView(data);
      } catch {
        // keep default
      } finally {
        setQuickSettingsLoading(false);
      }
    };
    load();
  }, [isOpen, selectedBusiness?.id, hasCalendarApp, showDefaultCalendarViewToolInChat]);

  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id || !hasCalendarApp || !supabase || !showBookingsByServiceAreaToolInChat) return;
    const load = async () => {
      setQuickSettingsLoading(true);
      try {
        const { data } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'mobile_booking_by_service_area',
          p_default_value: true,
        });
        if (data !== null && data !== undefined) setBookingsByServiceAreaEnabled(Boolean(data));
      } catch {
        // keep default
      } finally {
        setQuickSettingsLoading(false);
      }
    };
    load();
  }, [isOpen, selectedBusiness?.id, hasCalendarApp, showBookingsByServiceAreaToolInChat]);

  const handleClose = () => {
    setMessages([]);
    setQuestion('');
    setError(null);
    onClose();
  };

  const saveCalendarSetting = async (key: string, value: boolean) => {
    if (!selectedBusiness?.id || !supabase) return;
    try {
      setQuickSettingsLoading(true);
      const { error: err } = await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'calendar',
        p_setting_key: key,
        p_setting_value: value,
      });
      if (err) throw err;
      setError(null);
      if (key === 'show_location_booking_in_fab') {
        setShowLocationBookingInFAB(value);
        window.dispatchEvent(new CustomEvent('calendarSettingChanged', { detail: { key, value } }));
      } else {
        setShowMobileBookingInFAB(value);
        window.dispatchEvent(new CustomEvent('mobileCalendarSettingChanged', { detail: { key, value } }));
      }
    } catch {
      setError('Could not save setting. Try again from Calendar app settings.');
    } finally {
      setQuickSettingsLoading(false);
    }
  };

  const saveDefaultCalendarView = async (value: 'month' | 'week' | 'day') => {
    if (!selectedBusiness?.id || !supabase) return;
    try {
      setQuickSettingsLoading(true);
      const { error: err } = await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'calendar',
        p_setting_key: 'default_calendar_view',
        p_setting_value: value,
      });
      if (err) throw err;
      setError(null);
      setDefaultCalendarView(value);
      window.dispatchEvent(new CustomEvent('calendarSettingChanged', { detail: { key: 'default_calendar_view', value } }));
    } catch {
      setError('Could not save default view. Try again from Calendar app settings.');
    } finally {
      setQuickSettingsLoading(false);
    }
  };

  const saveBookingsByServiceArea = async (value: boolean) => {
    if (!selectedBusiness?.id || !supabase) return;
    try {
      setQuickSettingsLoading(true);
      const { error: err } = await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'calendar',
        p_setting_key: 'mobile_booking_by_service_area',
        p_setting_value: value,
      });
      if (err) throw err;
      setError(null);
      setBookingsByServiceAreaEnabled(value);
      window.dispatchEvent(new CustomEvent('calendarSettingChanged', { detail: { key: 'mobile_booking_by_service_area', value } }));
    } catch {
      setError('Could not save setting. Try again from Calendar app settings.');
    } finally {
      setQuickSettingsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || q.length < 5) {
      setError('Please ask a question (at least 5 characters).');
      return;
    }
    setQuestion('');
    await sendQuestion(q);
  };

  // Keep the ghost-choice listener pointed at the CURRENT session state and
  // send function (the listener itself is registered once).
  useEffect(() => {
    ghostChoiceGate.current = {
      decide: () => {
        if (!isOpen || voiceStatus === 'active' || voiceStatus === 'connecting') return 'ignore';
        return loading ? 'queue' : 'send';
      },
      send: (q: string) => void sendQuestion(q),
    };
  });

  const sendQuestion = async (q: string) => {
    setError(null);
    const userMessage: HelpMessage = { role: 'user', content: q };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const requestBody = {
        question: q,
        history: messages,
        businessId: selectedBusiness?.id ?? undefined,
        installedApps: installedAppNames,
        currentPath: window.location.pathname + window.location.search,
        actionCatalog: getAssistantActionCatalog(),
        // Forms (assistant surfaces) open RIGHT NOW — without this the model
        // only knows a form is open from chat history and sometimes "answers"
        // a follow-up tweak instead of calling the form tool.
        openSurfaces: getOpenAssistantSurfaces(),
        // The model has no clock — this grounds "tomorrow" / "march 3rd"
        clientNow: new Date().toLocaleString('en-US', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
        }),
      };
      // Agent loop: the model acts, we execute on screen, feed the results
      // back, and it continues — until it answers with no more actions (or we
      // hit the round cap). This is what lets it complete multi-step tasks
      // like creating a booking instead of narrating them.
      // One UI action per round means whole flows (booking: plus → type →
      // customer → verify) need plenty of rounds before the finalize summary.
      const MAX_ROUNDS = 10;
      const transcript: string[] = [];
      const actionsTaken: Array<{ label: string; ok: boolean }> = [];
      let data: any = null;
      let screenshotToSend = '';

      for (let round = 0; round < MAX_ROUNDS; round++) {
        const { data: roundData, error: fnError } = await supabase.functions.invoke('ai-help', {
          body: {
            ...requestBody,
            // The path can change between rounds as the AI navigates
            currentPath: window.location.pathname + window.location.search,
            agentTranscript: transcript.length > 0 ? transcript.join('\n') : undefined,
            screenshot: screenshotToSend || undefined,
          },
        });
        screenshotToSend = '';

        if (fnError || roundData?.error || roundData?.insufficientTokens) {
          if (roundData?.insufficientTokens) {
            setShowTokenPurchaseModal(true);
            setError(roundData?.error || 'Not enough AI tokens — purchase more in AI Settings to keep using Help.');
            setMessages((prev) => prev.slice(0, -1));
            setLoading(false);
            return;
          }
          if (data) break; // keep the last good round's answer
          setError(roundData?.error || 'Could not get an answer. Please try again.');
          setMessages((prev) => prev.slice(0, -1));
          setLoading(false);
          return;
        }
        data = roundData;

        const actions = Array.isArray(data?.actions) ? data.actions.slice(0, 5) : [];
        if (actions.length === 0) {
          // Hard no-claim gate: with an unsaved form open, a reply that claims
          // a change without a single tool call this turn is a hallucination —
          // re-ask ONCE with a corrective note so the model makes the call.
          const claimsChange =
            /\b(i'?ve\s+(set|changed|updated|adjusted|shortened|extended|moved|rescheduled|renamed)|is\s+now|now\s+(set|runs?|ends?|starts?|lasts?)|has\s+been\s+(set|changed|updated|adjusted|shortened|extended|moved|rescheduled))\b/i;
          if (
            round === 0 &&
            getOpenAssistantSurfaces().length > 0 &&
            claimsChange.test(String(data?.answer || ''))
          ) {
            transcript.push(
              'SYSTEM CHECK → your reply claimed a change but you called NO tools this turn, so nothing actually changed on the open form. Make the form tool call for the change now (set_task_schedule/set_task_details/set_booking_schedule/set_proposal_details/...), then answer from its result.',
            );
            continue;
          }
          break; // final answer — task done or nothing to do
        }

        // Only ONE UI-mutating action may run per round: the second click in a
        // "click A then click B" pair depends on what A revealed, so the model
        // must observe the result before B — otherwise it clicks blind.
        let uiActed = false;
        for (const action of actions) {
          if (!action || typeof action.name !== 'string') continue;
          const args = action.arguments && typeof action.arguments === 'object' ? action.arguments : {};

          const mutatesUi = action.name === 'click_element' || action.name === 'fill_field' || action.name === 'set_setting';
          if (mutatesUi && uiActed) {
            transcript.push(
              `${action.name}(${JSON.stringify(args)}) → SKIPPED: only one click/fill/set_setting runs per turn. Check the previous action's result (look_at_screen if unsure), then call this next turn if it is still the right step.`,
            );
            continue;
          }

          // Hard confirm-gate: Create Booking / Create Event / Create and review
          // (proposals) may never be pressed in the same user turn that filled
          // the form — the confirmation must arrive as a NEW message. On that
          // next turn no form tools have run yet, so the press goes through.
          // Enforced here because the model cannot be trusted to always obey
          // the prompt rule.
          const FORM_TOOLS = /^(start_booking|set_customer_source|add_booking_service|update_booking_service|set_booking_schedule|manage_booking_resources|set_booking_notes|set_booking_automations|create_vehicle|start_task|set_task_details|set_task_schedule|manage_task_resources|start_proposal|set_proposal_details|add_proposal_offer|update_proposal_offer|update_offer_service)\(/;
          const finalPress = String((args as Record<string, unknown>).text || '').match(/create\s*(booking|task)|create\s*and\s*review/i);
          if (
            action.name === 'click_element' &&
            finalPress &&
            transcript.some((line) => FORM_TOOLS.test(line))
          ) {
            const noun = finalPress[1] ? finalPress[1].toLowerCase() : 'proposal';
            const buttonLabel = noun === 'task' ? 'Create Event' : noun === 'booking' ? 'Create Booking' : 'Create and review';
            transcript.push(
              `${action.name}(${JSON.stringify(args)}) → BLOCKED: ${buttonLabel} is only pressed after the user confirms in a NEW message. Stop calling tools — reply telling the user the ${noun} is ready and asking whether to create it.`,
            );
            continue;
          }

          // Same idea for replies: reading a thread and sending into it within
          // one user turn means the wording is the model's own draft — the
          // approval must arrive as a NEW message.
          if (
            action.name === 'send_message' &&
            transcript.some((line) => line.startsWith('read_conversation('))
          ) {
            transcript.push(
              `send_message(${JSON.stringify(args)}) → BLOCKED: you read the thread this turn, so this wording is YOUR draft — the user has not approved it. Do not send. Stop calling tools and reply with the exact draft wording, asking whether to send it; send only after they say yes in a new message.`,
            );
            continue;
          }

          // call_person phones a real person — only legitimate when the
          // user's CURRENT message contains a call verb. "who CALLED me?" is
          // a question ("called"/"calls" don't match the bare verbs), and the
          // model demonstrably dials people after merely identifying them.
          if (action.name === 'call_person' && !/\b(call|phone|ring|dial)\b/i.test(q)) {
            transcript.push(
              `call_person(${JSON.stringify(args)}) → BLOCKED: the user's message did not ask you to place a call — answering a question about calls never dials anyone. Reply with the answer instead.`,
            );
            continue;
          }

          // mark_read all=true wipes unread state inbox-wide — only
          // legitimate when the user's CURRENT message explicitly says so.
          // "Mark that one as read" demonstrably gets escalated to mark-all.
          if (
            action.name === 'mark_read' &&
            ((args as Record<string, unknown>).all === true || (args as Record<string, unknown>).all === 'true') &&
            !/\b(all|everything|every|entire|whole|inbox)\b/i.test(q)
          ) {
            transcript.push(
              `mark_read(${JSON.stringify(args)}) → BLOCKED: the user did not say to mark EVERYTHING read — singular words like "that one"/"it"/a named sender mean ONE conversation. Call mark_read again for the single conversation they meant (the one just discussed), without all.`,
            );
            continue;
          }

          // Mass statement sends need the user's own approval in the CURRENT
          // message — the tool previews count+total without confirm, and the
          // model must not skip that step by passing confirm=true itself.
          if (
            action.name === 'send_statement' &&
            ((args as Record<string, unknown>).all === true || (args as Record<string, unknown>).all === 'true') &&
            ((args as Record<string, unknown>).confirm === true || (args as Record<string, unknown>).confirm === 'true') &&
            !/\b(yes|yeah|yep|sure|ok(ay)?|confirm(ed)?|go ahead|do it|send (them|it|all)|proceed)\b/i.test(q)
          ) {
            transcript.push(
              `send_statement(${JSON.stringify(args)}) → BLOCKED: confirm=true is only allowed AFTER the user approved the recipient count in their own message. Call send_statement with all=true WITHOUT confirm to get the count, tell the user, and wait for their yes.`,
            );
            continue;
          }

          // A resource assignment is only legitimate when the user actually
          // said the name — under pressure to reach READY the model invents
          // plausible names ("Mike Smith") that can match real team members.
          if (
            (action.name === 'manage_booking_resources' || action.name === 'manage_task_resources') &&
            String((args as Record<string, unknown>).action || 'add') !== 'remove'
          ) {
            const saidByUser = [q, ...messages.filter((m) => m.role === 'user').map((m) => m.content)]
              .join(' ')
              .toLowerCase();
            const resName = String((args as Record<string, unknown>).name || (args as Record<string, unknown>).query || '');
            const nameTokens = resName.toLowerCase().split(/\s+/).filter((t) => t.length > 1);
            if (nameTokens.length > 0 && !nameTokens.some((t) => saidByUser.includes(t))) {
              transcript.push(
                `${action.name}(${JSON.stringify(args)}) → BLOCKED: the user never said the name "${resName}" — assigning a team member they did not name is inventing. If a resource is still needed, ask "who's doing this job?" in your reply instead.`,
              );
              continue;
            }
          }

          if (mutatesUi) uiActed = true;

          if (action.name === 'look_at_screen') {
            const shot = await captureScreenshot();
            if (shot) {
              screenshotToSend = shot;
              transcript.push('look_at_screen → screenshot attached to your next turn.');
              if (!actionsTaken.some((a) => a.label === 'Looked at your screen')) {
                actionsTaken.push({ label: 'Looked at your screen', ok: true });
              }
            } else {
              transcript.push('look_at_screen → screenshot failed; rely on the other tool results.');
            }
            continue;
          }

          const result = await executeAssistantAction(action.name, args, {
            installedApps: installedAppNames,
            businessId: selectedBusiness?.id,
            source: 'text',
          });
          transcript.push(`${action.name}(${JSON.stringify(args)}) → ${result}`);
          if (action.name === 'query_business_data') {
            if (!actionsTaken.some((a) => a.label === 'Checked your live business data')) {
              actionsTaken.push({ label: 'Checked your live business data', ok: true });
            }
          } else {
            actionsTaken.push(describeAssistantAction(action.name, args, result));
          }
        }
      }

      // If the loop ran out of rounds mid-task, the last answer is a mid-work
      // placeholder — ask for a final no-tools summary so the reply honestly
      // reports what got done and what didn't.
      if (Array.isArray(data?.actions) && data.actions.length > 0 && transcript.length > 0) {
        const { data: wrapData, error: wrapError } = await supabase.functions.invoke('ai-help', {
          body: {
            ...requestBody,
            currentPath: window.location.pathname + window.location.search,
            agentTranscript: transcript.join('\n'),
            finalize: true,
          },
        });
        if (!wrapError && wrapData && !wrapData.error && wrapData.answer) {
          data = wrapData;
        }
      }

      const answerText = data?.answer ?? 'No answer returned.';
      const toolCalls = Array.isArray(data?.toolCalls) ? data.toolCalls : [];

      setMessages((prev) => [...prev, { role: 'assistant', content: answerText, toolCalls, actionsTaken }]);
    } catch {
      setError('Something went wrong. Please try again.');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-[10000] ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
        aria-hidden="true"
        data-assistant-capture-ignore="true"
      />
      <div
        data-assistant-capture-ignore="true"
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl z-[10001] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Help Centre</h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 min-h-0 flex flex-col">
            {/* Recent requests: this user's support requests + bug reports */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <Inbox className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-700">Recent requests</h3>
                {ticketsLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
              </div>
              {myTickets.length > 0 && (
                <div className="flex items-center gap-1 mb-2 bg-gray-100 rounded-lg p-0.5 w-fit">
                  <button
                    type="button"
                    onClick={() => { setTicketTab('open'); setShowAllTickets(false); setExpandedTicketId(null); }}
                    className={`px-3 py-1 rounded-md text-[12px] font-semibold transition-colors ${
                      ticketTab === 'open'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Open{openCount > 0 ? ` (${openCount})` : ''}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTicketTab('closed'); setShowAllTickets(false); setExpandedTicketId(null); }}
                    className={`px-3 py-1 rounded-md text-[12px] font-semibold transition-colors ${
                      ticketTab === 'closed'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Closed{closedCount > 0 ? ` (${closedCount})` : ''}
                  </button>
                </div>
              )}
              {myTickets.length === 0 ? (
                !ticketsLoading && (
                  <p className="text-xs text-gray-400">
                    No requests yet — support requests and bug reports you submit will show up here with their status.
                  </p>
                )
              ) : visibleTickets.length === 0 ? (
                <p className="text-xs text-gray-400">
                  {ticketTab === 'open'
                    ? 'No open requests — everything has been resolved.'
                    : 'No closed requests yet.'}
                </p>
              ) : (
                <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                  {(showAllTickets ? visibleTickets : visibleTickets.slice(0, 4)).map((t) => {
                    const meta = TICKET_STATUS_META[t.status] || { label: t.status, cls: 'bg-gray-100 text-gray-600' };
                    const expanded = expandedTicketId === t.id;
                    return (
                      <div key={t.id}>
                        <button
                          type="button"
                          onClick={() => expandTicket(t, expanded)}
                          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                        >
                          {expanded ? (
                            <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          )}
                          {t.kind === 'bug' ? (
                            <Bug className="h-4 w-4 text-red-400 shrink-0" />
                          ) : (
                            <Headphones className="h-4 w-4 text-green-500 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-gray-900 truncate">{t.title}</p>
                            <p className="text-[11px] text-gray-400">
                              {t.ticketNumber ? `${t.ticketNumber} · ` : ''}
                              {new Date(t.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              {t.adminReply ? ' · Nexus team replied' : ''}
                            </p>
                          </div>
                          <span className={`shrink-0 inline-flex px-2 py-0.5 text-[11px] font-semibold rounded-full ${meta.cls}`}>
                            {meta.label}
                          </span>
                        </button>
                        {expanded && t.kind === 'support' ? (
                          /* Support ticket: full conversation until closed */
                          <div className="px-3 pb-3 pl-9 space-y-2">
                            {t.ticketNumber && (
                              <p className="text-[11px] text-gray-400">
                                Ticket <span className="font-semibold text-gray-600">{t.ticketNumber}</span> — reference
                                this number if you contact us another way.
                              </p>
                            )}
                            {/* Original request */}
                            <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 ml-6">
                              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-0.5">You</p>
                              <p className="text-[13px] text-gray-800 whitespace-pre-wrap">{t.description}</p>
                            </div>
                            {/* Thread */}
                            {(threadByTicket[t.rawId] || []).map((m) => (
                              <div
                                key={m.id}
                                className={`rounded-lg px-3 py-2 ${
                                  m.sender === 'admin'
                                    ? 'bg-blue-50 border border-blue-100 mr-6'
                                    : 'bg-gray-50 border border-gray-100 ml-6'
                                }`}
                              >
                                <p className={`text-[11px] font-bold uppercase tracking-wide mb-0.5 ${
                                  m.sender === 'admin' ? 'text-blue-600' : 'text-gray-400'
                                }`}>
                                  {m.sender === 'admin' ? 'Nexus team' : 'You'}
                                  {' · '}
                                  {new Date(m.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                </p>
                                <p className="text-[13px] text-gray-800 whitespace-pre-wrap">{m.body}</p>
                              </div>
                            ))}
                            {threadLoading && (threadByTicket[t.rawId] || []).length === 0 && (
                              <div className="flex justify-center py-2">
                                <Loader2 className="h-4 w-4 animate-spin text-gray-300" />
                              </div>
                            )}
                            {/* Reply box (until closed) */}
                            {t.status === 'closed' ? (
                              <p className="text-[12px] text-gray-400">This ticket is closed.</p>
                            ) : (
                              <div className="flex items-end gap-2">
                                <textarea
                                  value={threadDraft}
                                  onChange={(e) => setThreadDraft(e.target.value)}
                                  rows={2}
                                  placeholder="Write a reply…"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => sendThreadMessage(t)}
                                  disabled={threadSending || !threadDraft.trim()}
                                  className="p-2.5 rounded-lg bg-gray-900 text-white disabled:opacity-40"
                                  aria-label="Send reply"
                                >
                                  {threadSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                </button>
                              </div>
                            )}
                          </div>
                        ) : expanded ? (
                          /* Bug report: description + single team reply */
                          <div className="px-3 pb-3 pl-9 space-y-2">
                            <p className="text-[13px] text-gray-600 whitespace-pre-wrap">{t.description}</p>
                            {t.adminReply ? (
                              <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
                                <p className="text-[11px] font-bold uppercase tracking-wide text-blue-600 mb-0.5">
                                  Reply from the Nexus team
                                  {t.repliedAt
                                    ? ` · ${new Date(t.repliedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                                    : ''}
                                </p>
                                <p className="text-[13px] text-gray-800 whitespace-pre-wrap">{t.adminReply}</p>
                              </div>
                            ) : (
                              <p className="text-[12px] text-gray-400">
                                {t.status === 'fixed'
                                  ? 'This bug has been fixed.'
                                  : 'Our team has received this and will follow up here.'}
                              </p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {visibleTickets.length > 4 && (
                    <button
                      type="button"
                      onClick={() => setShowAllTickets((v) => !v)}
                      className="w-full px-3 py-2 text-[12px] font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      {showAllTickets ? 'Show fewer' : `Show all (${visibleTickets.length})`}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons: Setup Wizard · Support Request · Report a Bug */}
            <div className="grid grid-cols-3 gap-2 mb-5">
              <button
                type="button"
                onClick={() => { onOpenSetupWizard?.(); handleClose(); }}
                className="relative flex flex-col items-center gap-2 px-2 py-3.5 rounded-xl border border-gray-200 bg-white hover:border-blue-400 hover:bg-blue-50 transition-all text-center group"
              >
                {wizardProgress && (
                  <span
                    className={`absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      wizardProgress.done >= wizardProgress.total
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {wizardProgress.done}/{wizardProgress.total}
                  </span>
                )}
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <Wand2 className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-gray-800 leading-tight">Setup Wizard</span>
              </button>
              <button
                type="button"
                onClick={() => setIsSupportRequestOpen(true)}
                className="flex flex-col items-center gap-2 px-2 py-3.5 rounded-xl border border-gray-200 bg-white hover:border-green-400 hover:bg-green-50 transition-all text-center group"
              >
                <div className="w-9 h-9 rounded-full bg-green-100 text-green-600 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <Headphones className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-gray-800 leading-tight">Support Request</span>
              </button>
              <button
                type="button"
                onClick={() => setIsReportBugOpen(true)}
                className="flex flex-col items-center gap-2 px-2 py-3.5 rounded-xl border border-gray-200 bg-white hover:border-red-400 hover:bg-red-50 transition-all text-center group"
              >
                <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                  <Bug className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-gray-800 leading-tight">Report a Bug</span>
              </button>
            </div>

            {needsDomainSetup && (
              <button
                type="button"
                onClick={() => {
                  handleClose();
                  window.dispatchEvent(new CustomEvent(OPEN_DOMAIN_HELPER_EVENT));
                }}
                className="mb-5 w-full flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 bg-white hover:border-indigo-400 hover:bg-indigo-50 transition-all text-center group"
              >
                <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                  <Globe className="h-5 w-5" />
                </div>
                <span className="text-sm font-medium text-gray-800">Configure Custom Domain</span>
              </button>
            )}

            {/* AI Help Section */}
            <div className="border-t border-gray-200 pt-4 flex-1 flex flex-col min-h-0">
              <div className="flex items-center gap-2 mb-3">
                <Bot className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-700">AI Help</h3>
                <button
                  type="button"
                  onClick={handleStartVoice}
                  disabled={voiceStatus === 'connecting'}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg text-xs font-semibold transition-colors touch-manipulation"
                  aria-label="Start voice conversation with AI help"
                >
                  {voiceStatus === 'connecting' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Mic className="h-3.5 w-3.5" />
                  )}
                  Voice Mode
                </button>
              </div>
              {voiceError && (
                <p className="text-xs text-red-500 mb-2">{voiceError}</p>
              )}
              {messages.length === 0 && (
                <p className="text-sm text-gray-500 mb-4">
                  Ask how to do something in the app. For example: &quot;How do I add a booking?&quot; or &quot;Where do I find invoices?&quot;
                </p>
              )}

            <div className="space-y-4 flex-1">
              {messages.map((msg, i) => (
                <div key={i} className="space-y-2">
                  <div
                    className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div
                      className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                        msg.role === 'user' ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                    </div>
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                        msg.role === 'user'
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-800 border border-gray-200'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">
                        {msg.role === 'assistant' && msg.content.includes('604')
                          ? (() => {
                              const supportNumber = '604-757-0793';
                              const telUrl = 'tel:+16047570793';
                              const parts = msg.content.split(/(604[- ]?757[- ]?0793)/);
                              return parts.map((part, j) =>
                                part.match(/^604[- ]?757[- ]?0793$/) ? (
                                  <a
                                    key={j}
                                    href={telUrl}
                                    className="font-medium text-blue-600 underline hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 rounded"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    {supportNumber}
                                  </a>
                                ) : (
                                  part
                                )
                              );
                            })()
                          : msg.content}
                      </p>
                    </div>
                  </div>
                  {/* Confirmation chips for app-control actions (opened screen / highlighted element) */}
                  {msg.role === 'assistant' && (msg.actionsTaken?.length ?? 0) > 0 && (
                    <div className="ml-10 flex flex-wrap gap-1.5">
                      {msg.actionsTaken!.map((action, j) => (
                        <span
                          key={j}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium border ${
                            action.ok
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}
                        >
                          <Wand2 className="h-3 w-3" />
                          {action.label}
                        </span>
                      ))}
                    </div>
                  )}
                  {msg.role === 'assistant' &&
                    hasCalendarApp &&
                    msg.toolCalls?.includes('show_calendar_fab_settings') && (
                    <div className="ml-10 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Settings className="h-4 w-4 text-gray-500" />
                        Quick settings - Calendar
                      </div>
                      <p className="text-xs text-gray-500">
                        Toggle these to show or hide options when you tap the blue plus icon.
                      </p>
                      <div className="space-y-2">
                        <label className="flex items-center justify-between gap-3 py-2 px-3 bg-white rounded-lg border border-gray-200">
                          <span className="text-sm text-gray-800">Location Booking - Show in floating action menu</span>
                          <button
                            type="button"
                            onClick={() => saveCalendarSetting('show_location_booking_in_fab', !showLocationBookingInFAB)}
                            disabled={quickSettingsLoading}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-50 ${
                              showLocationBookingInFAB ? 'bg-blue-500' : 'bg-gray-300'
                            }`}
                            aria-label={showLocationBookingInFAB ? 'Turn off Location Booking in FAB' : 'Turn on Location Booking in FAB'}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                                showLocationBookingInFAB ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </label>
                        <label className="flex items-center justify-between gap-3 py-2 px-3 bg-white rounded-lg border border-gray-200">
                          <span className="text-sm text-gray-800">Mobile Booking - Show in floating action menu</span>
                          <button
                            type="button"
                            onClick={() => saveCalendarSetting('show_mobile_booking_in_fab', !showMobileBookingInFAB)}
                            disabled={quickSettingsLoading}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-50 ${
                              showMobileBookingInFAB ? 'bg-blue-500' : 'bg-gray-300'
                            }`}
                            aria-label={showMobileBookingInFAB ? 'Turn off Mobile Booking in FAB' : 'Turn on Mobile Booking in FAB'}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                                showMobileBookingInFAB ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </label>
                      </div>
                    </div>
                  )}
                  {msg.role === 'assistant' &&
                    hasCalendarApp &&
                    msg.toolCalls?.includes('show_default_calendar_view_setting') && (
                    <div className="ml-10 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Settings className="h-4 w-4 text-gray-500" />
                        Default calendar view
                      </div>
                      <p className="text-xs text-gray-500">
                        Choose how the calendar opens: month, week, or day.
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {(['month', 'week', 'day'] as const).map((view) => (
                          <button
                            key={view}
                            type="button"
                            onClick={() => saveDefaultCalendarView(view)}
                            disabled={quickSettingsLoading}
                            className={`px-3 py-2 rounded-lg text-sm font-medium capitalize transition-colors disabled:opacity-50 ${
                              defaultCalendarView === view
                                ? 'bg-gray-900 text-white'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {view}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {msg.role === 'assistant' &&
                    hasCalendarApp &&
                    msg.toolCalls?.includes('show_bookings_by_service_area_setting') && (
                    <div className="ml-10 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Settings className="h-4 w-4 text-gray-500" />
                        Bookings by Service Area
                      </div>
                      <p className="text-xs text-gray-500">
                        When on, mobile booking only shows availability in your configured service areas (resources must be assigned to those areas). When off, mobile booking shows availability without filtering by area.
                      </p>
                      <label className="flex items-center justify-between gap-3 py-2 px-3 bg-white rounded-lg border border-gray-200">
                        <span className="text-sm text-gray-800">Require mobile bookings to match service areas</span>
                        <button
                          type="button"
                          onClick={() => saveBookingsByServiceArea(!bookingsByServiceAreaEnabled)}
                          disabled={quickSettingsLoading}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-50 ${
                            bookingsByServiceAreaEnabled ? 'bg-blue-500' : 'bg-gray-300'
                          }`}
                          aria-label={bookingsByServiceAreaEnabled ? 'Turn off Bookings by Service Area' : 'Turn on Bookings by Service Area'}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                              bookingsByServiceAreaEnabled ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </label>
                    </div>
                  )}
                  {msg.role === 'assistant' &&
                    hasCalendarApp &&
                    msg.toolCalls?.includes('show_all_calendar_settings') && (
                    <div className="ml-10 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <Settings className="h-4 w-4 text-gray-500" />
                        All calendar settings
                      </div>
                      <div className="space-y-2 text-xs text-gray-700">
                        <p className="font-medium text-gray-800">Action Menu (blue plus)</p>
                        <ul className="list-disc list-inside space-y-1 pl-1">
                          <li><strong>Location Booking - Show in floating action menu</strong> - Shows or hides &quot;Location Booking&quot; in the blue plus menu.</li>
                          <li><strong>Default Calendar View</strong> - Month, Week, or Day when you open the calendar.</li>
                          <li><strong>Mobile Booking - Show in floating action menu</strong> - Shows or hides &quot;Mobile Booking&quot; in the blue plus menu.</li>
                        </ul>
                        <p className="font-medium text-gray-800 pt-2">Mobile Bookings</p>
                        <ul className="list-disc list-inside space-y-1 pl-1">
                          <li><strong>Enable Map View</strong> - Map in day view with collapsible bookings.</li>
                          <li><strong>Resource Working on Job</strong> - Auto start/stop job timers by location proximity.</li>
                          <li><strong>Proximity Range (km)</strong> - How close (in km) for auto start/stop.</li>
                          <li><strong>Service Areas</strong> - Set up geographic service areas.</li>
                          <li><strong>Bookings by Service Area</strong> - Restrict mobile booking availability to service areas.</li>
                          <li><strong>Buffer Time</strong> - Time between mobile bookings for travel.</li>
                          <li><strong>Online Booking</strong> - Set up online booking for customers.</li>
                        </ul>
                      </div>
                      <p className="text-xs text-gray-500 border-t border-gray-200 pt-2 mt-2">
                        To change any of these: press and hold the Calendar app, tap the gear, then open the Settings tab. If you have any questions about a specific setting, just ask.
                      </p>
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-2">
                  <div className="shrink-0 w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Thinking...
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mt-2">{error}</p>
            )}

            <form onSubmit={handleSubmit} className="mt-4 shrink-0 space-y-2">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask a follow-up or new question..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                rows={2}
                disabled={loading}
                maxLength={500}
              />
              <button
                type="submit"
                disabled={loading || question.trim().length < 5}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Getting answer...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send
                  </>
                )}
              </button>
            </form>
            </div>
          </div>
        </div>
      </div>

      <ReportBugModal
        isOpen={isReportBugOpen}
        onClose={() => setIsReportBugOpen(false)}
      />

      <SupportRequestModal
        isOpen={isSupportRequestOpen}
        onClose={() => setIsSupportRequestOpen(false)}
      />

      <TokenPurchaseModal
        isOpen={showTokenPurchaseModal}
        onClose={() => setShowTokenPurchaseModal(false)}
        businessId={selectedBusiness?.id || ''}
        onSuccess={() => {
          setShowTokenPurchaseModal(false);
          setError(null);
        }}
      />
    </>
  );
}
